// See http://www.material-ui.com/#/customization/themes for the customisation guide
// More on Colors: http://www.material-ui.com/#/customization/colors

import { createMuiTheme } from '@material-ui/core/styles';
import { hex2rgb } from '../utils/color';

const colors = {
	alpha: '#2fa5e7',
	beta: '#4acfce',
	gamma: '#f7f7f7',
	delta: '#999999',
	epsilon: '#c7ebff',
	epsilonHover: '#a1bfcf',
	zeta: '#282828',
	eta: '#80ffff',
	theta: '#ff6060',
	white: '#ffffff',
	black: '#000000',
	gradient: ['#2778ff', '#2fa5e7'],
	purple: '#bb6efe',
	purpleHover: '#9056c2',
	darkPurple: '#632ACF',
	darkPurpleHover: '#5525b0',
	grey: 'rgba(40, 40, 40, 0.75)',
	error: '#bc1d1d'
};

const colorWithAlpha = (hex, alpha = 1) => {
	const { r, g, b } = hex2rgb(hex);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const defaultTheme = createMuiTheme({
	typography: {
		useNextVariants: true
	}
});

const muiTheme = createMuiTheme({
	typography: {
		useNextVariants: true,
		fontFamily: '"Open Sans Xara", Arial, Helvetica, sans-serif'
	},
	overrides: {
		MuiTypography: {
			h1: {
				fontFamily: 'Lato, "Open Sans Xara", Arial, Helvetica, sans-serif',
				fontWeight: 'bold',
				fontSize: '32px',
				lineHeight: '39px',
				color: colors.zeta
			},
			h2: {
				fontFamily: 'Lato, "Open Sans Xara", Arial, Helvetica, sans-serif',
				fontWeight: 'normal',
				fontSize: '28px',
				lineHeight: '34px',
				color: colors.zeta
			},
			h3: {
				fontFamily: 'Lato, "Open Sans Xara", Arial, Helvetica, sans-serif',
				fontWeight: 'normal',
				fontSize: '24px',
				lineHeight: '29px',
				color: colors.zeta
			},
			h4: {
				fontFamily: 'Lato, "Open Sans Xara", Arial, Helvetica, sans-serif',
				fontWeight: 'bold',
				fontSize: '16px',
				lineHeight: '28px',
				color: colors.zeta
			},
			caption: {
				fontFamily: 'Lato, "Open Sans Xara", Arial, Helvetica, sans-serif',
				fontWeight: 'bold',
				fontSize: '12px',
				lineHeight: '15px',
				color: colors.zeta,
				textTransform: 'uppercase'
			},
			subtitle1: {
				fontFamily: '"Open Sans Xara", Arial, Helvetica, sans-serif',
				fontWeight: 'normal',
				fontSize: '18px',
				lineHeight: '24px',
				color: colorWithAlpha(colors.zeta, 0.9)
			},
			body1: {
				fontFamily: '"Open Sans Xara", Arial, Helvetica, sans-serif',
				fontWeight: 'normal',
				fontSize: '16px',
				lineHeight: '22px',
				color: colorWithAlpha(colors.zeta, 0.75)
			},
			body2: {
				fontFamily: '"Open Sans Xara", Arial, Helvetica, sans-serif',
				fontWeight: 'normal',
				fontSize: '13px',
				lineHeight: '18px',
				opacity: 0.75
			}
		},
		MuiAppBar: {
			colorPrimary: {
				backgroundColor: 'transparent',
				backgroundImage: `linear-gradient(to bottom, ${colors.gradient[0]} 0%, ${colors.gradient[1]} 100%);`,
				boxShadow: `0 2px 3px ${colorWithAlpha(defaultTheme.palette.common.black, 0.25)}`
			}
		},
		MuiButton: {
			root: {
				borderRadius: '21px',
				textTransform: 'none',
				fontFamily: 'Lato, "Open Sans Xara", Arial, Helvetica, sans-serif',
				fontSize: '16px',
				padding: '6px 25px',
				minWidth: '120px',
				whiteSpace: 'nowrap',
				color: colors.delta,
				transition: defaultTheme.transitions.create(['background-color', 'box-shadow', 'border', 'color']),
				textDecoration: 'underline',
				'&:hover': {
					color: colors.alpha,
					backgroundColor: 'transparent',
					textDecoration: 'underline'
				}
			},
			contained: {
				boxShadow: 'none',
				color: defaultTheme.palette.common.white,
				backgroundColor: '#c7c7c7',
				textShadow: `1px 1px 2px ${colorWithAlpha(defaultTheme.palette.common.black, 0.18)}`,
				textDecoration: 'none',
				'&$disabled': {
					color: defaultTheme.palette.common.white,
					backgroundColor: colorWithAlpha(defaultTheme.palette.common.black, 0.26)
				},
				'&:hover': {
					color: defaultTheme.palette.common.white,
					textDecoration: 'none',
					backgroundColor: '#8b8a8a',
					boxShadow: `1px 1px 3px ${colorWithAlpha(defaultTheme.palette.common.black, 0.16)}`
				}
			},
			containedPrimary: {
				backgroundColor: colors.alpha,
				'&$disabled': {
					backgroundColor: colorWithAlpha(colors.alpha, 0.32),
					color: defaultTheme.palette.common.white
				},
				'&:hover': {
					backgroundColor: '#1d79ac',
					boxShadow: `1px 1px 3px ${colorWithAlpha(defaultTheme.palette.common.black, 0.16)}`
				}
			},
			containedSecondary: {
				backgroundColor: colors.theta,
				'&$disabled': {
					backgroundColor: colorWithAlpha('#d54f4f', 0.3),
					color: defaultTheme.palette.common.white
				},
				'&:hover': {
					backgroundColor: '#d54f4f',
					boxShadow: 'none'
				}
			},
			outlinedPrimary: {
				color: colors.black,
				borderColor: colors.alpha,
				borderWidth: 2,
				textDecoration: 'none',
				padding: '4px 16px',
				'&:hover': {
					color: '#000',
					borderWidth: 2,
					backgroundColor: '#fff',
					borderColor: '#2888C3',
					textDecoration: 'none'
				}
			},
			sizeSmall: {
				padding: '6px 15px',
				borderRadius: '18px',
				fontSize: '13px'
			},
			sizeLarge: {
				minWidth: '210px'
			}
		},
		MuiInput: {
			root: {
				fontFamily: '"Open Sans Xara", Arial, Helvetica, sans-serif'
			},
			input: {
				width: '100%',
				fontSize: '13px',
				border: `1px solid ${colorWithAlpha(colors.delta, 0.32)}`,
				padding: '7px 17px',
				borderRadius: '20px',
				color: colorWithAlpha(colors.zeta, 0.75),
				background: defaultTheme.palette.common.white,
				transition: defaultTheme.transitions.create(['box-shadow', 'border-color']),
				'&::-webkit-input-placeholder': {
					color: colors.delta,
					fontStyle: 'italic'
				},
				'&::-moz-placeholder': {
					color: colors.delta,
					fontStyle: 'italic'
				},
				'&:-ms-input-placeholder': {
					color: colors.delta,
					fontStyle: 'italic',
					opacity: 1
				},
				'&::-ms-input-placeholder': {
					color: colors.delta,
					fontStyle: 'italic',
					opacity: 1
				},
				'&:focus': {
					borderColor: colors.epsilon,
					boxShadow: `1px 1px 2px ${colors.epsilon}`
				},
				'&$disabled': {
					color: colorWithAlpha(colors.delta, 0.85)
				},
				'&:-webkit-autofill': {
					'-webkit-box-shadow': '0 0 0 30px white inset !important',
					'-webkit-text-fill-color': `${colorWithAlpha(colors.zeta, 0.75)} !important`
				},
				'&:-webkit-autofill:hover': {
					'-webkit-box-shadow': '0 0 0 30px white inset !important',
					'-webkit-text-fill-color': `${colorWithAlpha(colors.zeta, 0.75)} !important`
				},
				'&:-webkit-autofill:focus': {
					'-webkit-box-shadow': '0 0 0 30px white inset !important',
					'-webkit-text-fill-color': `${colorWithAlpha(colors.zeta, 0.75)} !important`
				},
				'&:-webkit-autofill:active': {
					'-webkit-box-shadow': '0 0 0 30px white inset !important',
					'-webkit-text-fill-color': `${colorWithAlpha(colors.zeta, 0.75)} !important`
				}
			},
			fullWidth: {
				width: '100%'
			},
			multiline: {
				padding: 0
			}
		},
		MuiFormLabel: {
			asterisk: {
				color: '#db3131',
				'&$error': {
					color: '#db3131'
				},
			}
		},		
		MuiFormHelperText: {
			root: {
				'&$error': {
					color: colors.error,
					fontStyle: 'italic',
					paddingLeft: '20px',
					marginBottom: '5px'
				}
			}
		},
		MuiSelect: {
			select: {
				borderRadius: '20px',
				paddingRight: '40px',
				boxShadow: `1px 1px 2px ${colorWithAlpha(defaultTheme.palette.common.black, 0.16)}`,
				'&:focus': {
					borderRadius: '20px',
					backgroundColor: defaultTheme.palette.common.white,
					boxShadow: `1px 1px 2px ${colorWithAlpha(defaultTheme.palette.common.black, 0.16)}`,
					borderColor: colorWithAlpha(colors.delta, 0.32)
				}
			},
			icon: {
				right: '14px',
				color: colors.delta
			}
		},
		MuiMenu: {
			paper: {
				padding: 0,
				borderColor: colorWithAlpha(colors.delta, 0.16),
				borderRadius: 18,
				boxShadow: `1px 1px 2px ${colorWithAlpha(defaultTheme.palette.common.black, 0.16)}`
			}
		},
		MuiMenuItem: {
			root: {
				fontFamily: '"Open Sans Xara", Arial, Helvetica, sans-serif',
				fontSize: '13px',
				color: colorWithAlpha(colors.zeta, 0.75),
				'&$selected, &$selected:hover, &$selected:focus': {
					background: 'none'
				}
			}
		},
		MuiDivider: {
			root: {
				backgroundColor: colorWithAlpha(colors.delta, 0.2)
			}
		},
		MuiBackdrop: {
			root: {
				backgroundColor: colorWithAlpha(defaultTheme.palette.common.white, 0.85)
			}
		},
		MuiDialog: {
			paper: {
				boxShadow: 'none',
				borderRadius: '9px',
				border: `1px solid ${colors.alpha}`
			},
			paperWidthSm: {
				maxWidth: 330
			}
		},
		MuiDialogTitle: {
			root: {
				padding: '20px 20px 0',
				textAlign: 'center'
			}
		},
		MuiDialogContent: {
			root: {
				padding: '10px 20px 0',
				textAlign: 'center',
				'&:first-child': {
					paddingTop: 14
				},
				'&:last-child': {
					paddingBottom: 14
				}
			}
		},
		MuiDialogContentText: {
			root: {
				color: colorWithAlpha(colors.zeta, 0.75)
			}
		},
		MuiDialogActions: {
			root: {
				margin: '15px 20px 20px',
				justifyContent: 'center'
			},
			action: {
				margin: '0 10px'
			}
		},
		MuiLink: {
			root: {
				cursor: 'pointer',
				display: 'inline-block'
			}
		},
		MuiPopover: {
			paper: {
				padding: '18px 28px',
				boxShadow: 'none',
				borderRadius: '9px',
				border: `1px solid ${colors.alpha}`,
				textAlign: 'center'
			}
		},
		MuiTabs: {
			root: {
				color: colorWithAlpha(colors.zeta, 0.75)
			},
			indicator: {
				display: 'none'
			}
		},
		MuiTab: {
			root: {
				borderRadius: '15px 15px 0 0',
				transition: defaultTheme.transitions.create('background-color'),
				textTransform: 'none',
				fontSize: '16px',
				[defaultTheme.breakpoints.up('md')]: {
					fontSize: '16px',
					minWidth: 160
				}
			},
			textColorInherit: {
				opacity: 1,
				'&$selected': {
					fontWeight: 'bold',
					backgroundColor: defaultTheme.palette.common.white
				}
			},
			labelIcon: {
				minHeight: 48,
				paddingTop: 0
			},
			labelContainer: {
				[defaultTheme.breakpoints.up('md')]: {
					padding: '6px 12px',
				}
			}
		},
		MuiTableRow: {
			root: {
				transition: defaultTheme.transitions.create('background-color'),
				'&$hover:hover': {
					backgroundColor: colorWithAlpha(colors.epsilon, 0.16)
				}
			}
		},
		MuiTableCell: {
			root: {
				textOverflow: 'ellipsis',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				borderBottom: `1px solid ${colorWithAlpha(colors.zeta, 0.15)}`
			},
			head: {
				color: colorWithAlpha(colors.zeta, 0.75),
				fontSize: '13px',
				fontWeight: 'bold'
			},
			body: {
				color: colorWithAlpha(colors.zeta, 0.75),
				fontSize: '13px'
			}
		},
		MuiIconButton: {
			root: {
				padding: 6
			},
			colorSecondary: {
				color: colors.delta,
				transition: defaultTheme.transitions.create('color'),
				'&:hover': {
					backgroundColor: 'transparent',
					color: colors.theta
				}
			}
		}
	},
	palette: {
		colors: { ...colors, colorWithAlpha },
		primary: {
			main: colors.alpha,
			contrastText: defaultTheme.palette.common.white
		},
		secondary: {
			main: colors.theta,
			contrastText: defaultTheme.palette.common.white
		},
		tertiary: {
			main: colors.alpha,
			contrastText: defaultTheme.palette.common.white
		},
		background: {
			'default': defaultTheme.palette.common.white
		}
	},
	zIndex: {
		appBar: 1250,
		xaraLogo: 1250,
		stickyHeader: 1000
	}
});

export default muiTheme;


// WEBPACK FOOTER //
// ./src/theme/muiTheme.js